// Cross-Skill Diagnostic — the connected-skills chain visuals (top row of the view). Renders the
// deterministic `high_level_summary.graphs` the worker emits: a 'lift' chain ("what worked") and/or a
// 'drag' chain ("what held the call back"), each a linear root→flagship path of band-coloured skill
// nodes with arrows. The picture is a visual echo of the narrative paragraphs below it.
//
// Phase 2 depth: every node carries its dependency `note` (implication) and every edge the pairwise
// coaching `note` — revealed on hover (mouse) or click/tap (touch) in a shared tooltip.
//
// Hand-rolled inline SVG (no library). All content is server-derived from a fixed skill set, built via
// createElementNS + textContent (never innerHTML) to match the app's no-raw-HTML safety posture.

import type { HighLevelGraph, SkillBand } from '../lib/types.js';

const SVGNS = 'http://www.w3.org/2000/svg';

const BAND_COLOR: Record<SkillBand, string> = {
  low: '#d64a41',
  med: '#c07a00',
  high: '#2a9d57',
  unscored: '#8a8a8a',
};
// SVG helper — only the idle demo animation is still SVG; the live chains are HTML boxes now.
function el(tag: string, attrs: Record<string, string>, text?: string): SVGElement {
  const node = document.createElementNS(SVGNS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  if (text !== undefined) node.textContent = text;
  return node;
}

/** A shared tooltip: shown near a hovered/clicked node or edge, hidden on leave or outside click. */
function makeTooltip(wrapper: HTMLElement): HTMLElement {
  const tip = document.createElement('div');
  tip.className = 'cs-tip';
  tip.style.display = 'none';
  wrapper.appendChild(tip);
  return tip;
}

function attachTip(target: HTMLElement | SVGElement, note: string | undefined, wrapper: HTMLElement, tip: HTMLElement): void {
  if (!note) return;
  (target as HTMLElement).style.cursor = 'help';
  const show = (): void => {
    tip.textContent = note;
    tip.style.display = 'block';
    const wr = wrapper.getBoundingClientRect();
    const tr = target.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();
    let left = tr.left - wr.left + tr.width / 2 - tipRect.width / 2;
    let top = tr.top - wr.top - tipRect.height - 8;
    left = Math.max(4, Math.min(left, wr.width - tipRect.width - 4));
    if (top < 0) top = tr.bottom - wr.top + 8; // flip below when no room above
    tip.style.left = `${left}px`;
    tip.style.top = `${top}px`;
  };
  const hide = (): void => {
    tip.style.display = 'none';
  };
  target.addEventListener('pointerenter', (e) => {
    if ((e as PointerEvent).pointerType === 'mouse') show();
  });
  target.addEventListener('pointerleave', (e) => {
    if ((e as PointerEvent).pointerType === 'mouse') hide();
  });
  target.addEventListener('click', (e) => {
    e.stopPropagation();
    if (tip.style.display === 'block' && tip.textContent === note) hide();
    else show();
  });
}

const BAND_WORD: Record<string, string> = { low: 'Below', med: 'Near', high: 'At / above', unscored: 'not scored' };

/** Build one chain as a row of HTML boxes (Runtime-Monitor look: solid band fill + soft glow) joined
 *  by arrows, wiring node/edge tooltips into the shared `tip`. HTML (not SVG) so the fill + glow match
 *  the Runtime Monitor and the row aligns to the column's left edge (same as the standalone boxes). */
function renderGraph(graph: HighLevelGraph, wrapper: HTMLElement, tip: HTMLElement, scores: Record<string, string>): HTMLElement {
  const block = document.createElement('div');
  block.className = 'cs-graph-block';
  const flow = document.createElement('div');
  flow.className = `cs-flow cs-flow-${graph.kind}`;

  const nodes = graph.nodes;
  nodes.forEach((node, i) => {
    if (i > 0) {
      const arrow = document.createElement('span');
      arrow.className = 'cs-arrow';
      arrow.textContent = '→';
      const edge = graph.edges.find((e) => e.to === node.id && e.from === nodes[i - 1]!.id);
      attachTip(arrow, edge?.note, wrapper, tip);
      flow.appendChild(arrow);
    }
    const wrap = document.createElement('div');
    wrap.className = 'cs-node-wrap';
    if (node.id === graph.root) {
      const badge = document.createElement('span');
      badge.className = 'cs-node-badge';
      badge.textContent = 'ROOT CAUSE';
      wrap.appendChild(badge);
    }
    const box = document.createElement('div');
    box.className = `cs-node cs-node-${node.band}`;
    if (node.id === graph.flagship) {
      const tag = document.createElement('span');
      tag.className = 'cs-node-tag';
      tag.textContent = '★ Outcome';
      box.appendChild(tag);
    }
    const nm = document.createElement('span');
    nm.className = 'cs-node-nm';
    nm.textContent = node.name;
    box.appendChild(nm);
    const sub = document.createElement('span');
    sub.className = 'cs-node-sub';
    sub.textContent = scores[node.id] || BAND_WORD[node.band] || node.band;
    box.appendChild(sub);
    attachTip(box, node.note, wrapper, tip);
    wrap.appendChild(box);
    flow.appendChild(wrap);
  });

  block.appendChild(flow);
  return block;
}

export interface ChainChip { name: string; score: string; band: SkillBand; outcome?: boolean }
export interface ChainsData {
  /** The positive dependency chain, or null when no run of strengths compounded. */
  lift: HighLevelGraph | null;
  /** The root-cause drag chain, or null when the weak skills didn't form a connected chain. */
  drag: HighLevelGraph | null;
  /** Strengths that did NOT set up / depend on another skill — shown as standalone boxes, no arrows. */
  strengthChips: ChainChip[];
  /** Weak skills that didn't feed the drag chain — shown as standalone boxes rather than forced on a line. */
  gapChips: ChainChip[];
  /** skill_id → formatted score, so chain nodes can show the number under the name. */
  scores: Record<string, string>;
}

/** A standalone strength/gap: the SAME box as a chain node (solid fill + glow), just no arrow into it
 *  (evidence-gated — we never draw a link the call didn't show). */
function chip(item: ChainChip): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'cs-node-wrap';
  const box = document.createElement('div');
  box.className = `cs-node cs-node-${item.band}`;
  if (item.outcome) {
    const tag = document.createElement('span');
    tag.className = 'cs-node-tag';
    tag.textContent = '★ Outcome';
    box.appendChild(tag);
  }
  const nm = document.createElement('span');
  nm.className = 'cs-node-nm';
  nm.textContent = item.name;
  box.appendChild(nm);
  if (item.score) {
    const s = document.createElement('span');
    s.className = 'cs-node-sub';
    s.textContent = item.score;
    box.appendChild(s);
  }
  wrap.appendChild(box);
  return wrap;
}

/** The shared chains legend. Rendered beside the "The call, connected" header (see results.ts). */
export function crossSkillLegend(): HTMLElement {
  const legend = document.createElement('div');
  legend.className = 'cs-legend cs-legend-band';
  const item = (color: string, label: string): void => {
    const span = document.createElement('span');
    span.className = 'cs-legend-item';
    const dot = document.createElement('i');
    dot.className = 'cs-legend-dot';
    dot.style.background = color;
    span.appendChild(dot);
    span.appendChild(document.createTextNode(label));
    legend.appendChild(span);
  };
  item(BAND_COLOR.low, 'below target');
  item(BAND_COLOR.med, 'near target');
  item(BAND_COLOR.high, 'at / above target');
  const enables = document.createElement('span');
  enables.className = 'cs-legend-item';
  enables.textContent = '→ feeds the next skill';
  legend.appendChild(enables);
  const hint = document.createElement('span');
  hint.className = 'cs-legend-item cs-legend-hint-inline';
  hint.textContent = 'Arrows appear only where the call shows a real link · hover a box for detail';
  legend.appendChild(hint);
  return legend;
}

/** Render the two connected-skill columns (moved the needle / held the call back) with the legend on
 *  top. Evidence-gated: a real chain draws as a chain; standalone skills render as chips, never forced
 *  onto the line. No prose inside these containers (the story is folded into Score Analysis). Hover
 *  keeps the per-node / per-link tooltips exactly as before. */
export function renderCrossSkillChains(host: HTMLElement, data: ChainsData): void {
  host.replaceChildren();
  const wrap = document.createElement('div');
  wrap.className = 'cs-chains';
  const tip = makeTooltip(wrap);

  const grid = document.createElement('div');
  grid.className = 'cs-chains-grid';

  const column = (title: string, kind: 'good' | 'bad', graph: HighLevelGraph | null, chips: ChainChip[], emptyMsg: string): HTMLElement => {
    const col = document.createElement('div');
    col.className = 'cs-chain-col';
    const h = document.createElement('h4');
    h.className = `cs-chain-t cs-chain-${kind}`;
    h.textContent = title;
    col.appendChild(h);
    if (graph) col.appendChild(renderGraph(graph, wrap, tip, data.scores));
    if (chips.length) {
      const box = document.createElement('div');
      box.className = 'cs-chips';
      for (const c of chips) box.appendChild(chip(c));
      col.appendChild(box);
    }
    if (!graph && !chips.length) {
      const p = document.createElement('p');
      p.className = 'cs-chain-empty';
      p.textContent = emptyMsg;
      col.appendChild(p);
    }
    return col;
  };

  grid.appendChild(column('What moved the needle', 'good', data.lift, data.strengthChips, 'Nothing cleared the bar to build on here.'));
  grid.appendChild(column('What held the call back', 'bad', data.drag, data.gapChips, 'No connected drag on this call.'));
  wrap.appendChild(crossSkillLegend()); // legend band across the top of the frame, above both columns
  wrap.appendChild(grid);

  // Click outside a node/edge hides the tooltip.
  wrap.addEventListener('click', () => { tip.style.display = 'none'; });
  host.appendChild(wrap);
}

/** Idle-state demo (no data yet): a "flowing chain" preview that speaks the real output's own language
 *  — a left-to-right row of ghosted skill cards joined by arrowed connectors, with a single calm gold
 *  "current" flowing through them (each card warms to gold as it passes). It previews the connected-
 *  skills chain the lens produces, rather than a decorative web. Pure SVG (aria-hidden). */
export function renderCrossSkillDemo(host: HTMLElement): void {
  host.textContent = '';
  const VB_W = 436;
  const VB_H = 148;
  const svg = el('svg', { viewBox: `0 0 ${VB_W} ${VB_H}`, width: String(VB_W), height: String(VB_H), class: 'cs-demo-svg', 'aria-hidden': 'true' });
  const GREY = 'rgba(45,227,203,.35)'; // ghosted card outline (dark HUD — teal hairline)
  const LINE = 'rgba(214,239,236,.45)'; // connectors (light on the dark ground)
  const cardW = 80;
  const cardH = 60;
  const gap = 28;
  const x0 = 16;
  const yTop = 30;
  const midY = yTop + cardH / 2;
  const T = 5; // seconds — calm, unflashy
  const S = (n: number): string => n.toFixed(1);
  const leftEdge = (i: number): number => x0 + i * (cardW + gap);
  const centre = (i: number): number => leftEdge(i) + cardW / 2;

  // Connectors (line + arrowhead) between consecutive cards.
  for (let i = 0; i < 3; i++) {
    const xa = leftEdge(i) + cardW;
    const xb = leftEdge(i + 1);
    svg.appendChild(el('line', { x1: S(xa), y1: S(midY), x2: S(xb - 5), y2: S(midY), stroke: LINE, 'stroke-width': '1.4' }));
    svg.appendChild(el('path', { d: `M ${S(xb - 9)} ${S(midY - 4)} L ${S(xb - 2)} ${S(midY)} L ${S(xb - 9)} ${S(midY + 4)}`, fill: 'none', stroke: LINE, 'stroke-width': '1.4', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }));
  }

  // Path the "current" travels; used to time each card's warm-to-gold as the dot arrives.
  const pathStart = 8;
  const pathEnd = VB_W - 8;
  const range = pathEnd - pathStart;
  const k = (v: number): string => Math.max(0, Math.min(1, v)).toFixed(3);

  // Ghosted skill cards (skeleton content), each briefly warming to gold as the current passes.
  for (let i = 0; i < 4; i++) {
    const rx = leftEdge(i);
    const arrival = (centre(i) - pathStart) / range;
    const card = el('rect', { x: S(rx), y: S(yTop), width: S(cardW), height: S(cardH), rx: '8', fill: '#0e1a1c', stroke: GREY, 'stroke-width': '1.5' });
    card.appendChild(el('animate', { attributeName: 'stroke', values: `${GREY};${GREY};#ffbe17;${GREY};${GREY}`, keyTimes: `0;${k(arrival - 0.07)};${k(arrival)};${k(arrival + 0.07)};1`, dur: `${T}s`, repeatCount: 'indefinite' }));
    svg.appendChild(card);
    svg.appendChild(el('rect', { x: S(rx + 14), y: S(yTop + 20), width: S(cardW - 28), height: '5', rx: '2.5', fill: 'rgba(214,239,236,.18)' }));
    svg.appendChild(el('rect', { x: S(rx + 14), y: S(yTop + 34), width: S(cardW - 40), height: '5', rx: '2.5', fill: 'rgba(214,239,236,.10)' }));
  }

  // The "current": a gold dot (with a soft glow) flowing left -> right through the chain, looping.
  const motionPath = `M ${S(pathStart)} ${S(midY)} L ${S(pathEnd)} ${S(midY)}`;
  const glow = el('circle', { r: '9', fill: '#ffbe17', opacity: '0.16' });
  glow.appendChild(el('animateMotion', { dur: `${T}s`, repeatCount: 'indefinite', path: motionPath, calcMode: 'linear' }));
  svg.appendChild(glow);
  const dot = el('circle', { r: '4.5', fill: '#ffbe17' });
  dot.appendChild(el('animateMotion', { dur: `${T}s`, repeatCount: 'indefinite', path: motionPath, calcMode: 'linear' }));
  svg.appendChild(dot);

  host.appendChild(svg);
}
